import React, { useEffect } from 'react';
import { FileText, Lock, RotateCcw, HelpCircle, Phone, Mail, MessageCircle, Info } from 'lucide-react';
import { whatsappUrl, WHATSAPP_DISPLAY, PHONE_TEL } from '../../config/whatsapp';
import packageJson from '../../../package.json';

/* ═══════════════════════════════════════════════════════════
   ModalBase — shared bottom-sheet / centre-card modal
   • Locks body scroll while open
   • Closes on Escape key or backdrop click
   • RTL, responsive: bottom-sheet on mobile, centred on desktop
═══════════════════════════════════════════════════════════ */
export const ModalBase = ({ title, icon = <Info size={18} />, onClose, children, onAccept, acceptLabel = 'فهمت، أوافق' }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', esc); };
  }, [onClose]);

  return (
    <div dir="rtl" className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full sm:max-w-lg bg-white rounded-t-[28px] sm:rounded-2xl shadow-2xl max-h-[90dvh] sm:max-h-[80vh] flex flex-col animate-slide-down"
        style={{ animation: 'slideDown 0.22s ease both' }}
      >
        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 sm:hidden flex-shrink-0">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-[#EEF2FF] text-[#0A4D9D] flex items-center justify-center flex-shrink-0">{icon}</span>
            <h2 className="text-[17px] font-black text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="إغلاق"
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors text-lg font-bold"
          >
            ×
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4 text-sm text-gray-700 leading-relaxed">
          {children}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 bg-gray-50 rounded-b-[28px] sm:rounded-b-2xl">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-100 transition-colors"
            >
              إغلاق
            </button>
            {onAccept && (
              <button
                onClick={() => { onAccept(); onClose(); }}
                className="flex-1 py-2.5 bg-[#0A4D9D] hover:bg-[#083d7e] text-white font-bold rounded-xl text-sm transition-colors"
              >
                {acceptLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────── Shared content blocks ─────────────────────── */
const Section = ({ title, children }) => (
  <div>
    <h3 className="font-black text-gray-900 text-[15px] mb-2">{title}</h3>
    <div className="text-gray-600 text-sm leading-relaxed space-y-1.5">{children}</div>
  </div>
);

const Li = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#0A4D9D] flex-shrink-0" />
    <span>{children}</span>
  </li>
);

// Flags a sentence whose specifics (fee %, day count, etc.) are not yet
// business/legal-approved — renders as a visible notice rather than
// silently stating an invented number as policy.
const PendingApprovalNotice = ({ children }) => (
  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-3 text-xs text-amber-800 mt-2">
    <Info size={14} className="flex-shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

// Genuinely a new revision as of this edit — not a stale placeholder date.
const lastUpdated = 'آخر تحديث: أكتوبر 2026';
const SUPPORT_EMAIL = 'support@safer.sd';

/* ═══════════════════════════════════════════════════════════
   TermsModal
═══════════════════════════════════════════════════════════ */
export const TermsModal = ({ onClose, onAccept }) => (
  <ModalBase title="الشروط والأحكام" icon={<FileText size={16} />} onClose={onClose} onAccept={onAccept}>
    <p className="text-xs text-gray-500">{lastUpdated}</p>

    <Section title="1. القبول والموافقة">
      <p>باستخدام تطبيق سافر أو موقعه الإلكتروني، يوافق المستخدم على الشروط والأحكام والسياسات المعمول بها للخدمات التي يختار استخدامها.</p>
    </Section>

    <Section title="2. الخدمات المقدمة">
      <ul className="space-y-1.5 list-none">
        <Li>حجز رحلات الباص بين المدن.</Li>
        <Li>طلب المشاوير داخل المدينة.</Li>
        <Li>إرسال الطرود.</Li>
        <Li>طلب خدمات نقل البضائع.</Li>
      </ul>
    </Section>

    <Section title="3. حساب المستخدم">
      <ul className="space-y-1.5 list-none">
        <Li>يمكن استخدام خدمة حجز رحلات الباص كضيف دون إنشاء حساب.</Li>
        <Li>تتطلب خدمات المشاوير وإرسال الطرود ونقل البضائع حساباً مسجلاً.</Li>
        <Li>تتطلب خدمة إرسال الطرود، إضافة إلى الحساب المسجَّل، تذكرة سفر سارية على الرحلة المراد إرسال الطرد عليها.</Li>
        <Li>يتم التحقق من الحسابات الجديدة باستخدام إجراء التحقق عبر واتساب المعتمد حالياً.</Li>
        <Li>يتحمل المستخدم مسؤولية المحافظة على سرية الرقم السري وبيانات الدخول.</Li>
        <Li>يجب تقديم بيانات صحيحة عند إنشاء الحساب أو تنفيذ الطلبات.</Li>
      </ul>
    </Section>

    <Section title="4. شروط حجز رحلات الباص">
      <p>يلتزم المسافر بإدخال بيانات صحيحة ومطابقة للوثائق الرسمية. يعتمد تأكيد الحجز على توفر المقعد وإتمام الدفع وفق التعليمات المعروضة، وتصدر التذكرة الإلكترونية بعد تأكيد الشركة الناقلة استلام المبلغ. قد تُطلب بيانات إثبات الهوية عند الصعود وفق سياسة الشركة الناقلة. تخضع التعديلات والإلغاءات لسياسة الإلغاء والاسترداد الخاصة بكل شركة، وتقع مسؤولية تشغيل الرحلة والالتزام بمواعيدها على عاتق الشركة الناقلة.</p>
    </Section>

    <Section title="5. شروط المشاوير داخل المدينة">
      <p>تتطلب خدمة المشاوير تسجيل الدخول وتحديد نقطة الانطلاق والوجهة. تتوقف إتاحة السائقين والمركبات على التغطية الجغرافية ووقت الطلب. تُعرض الأجرة أو آلية احتسابها قبل تأكيد الطلب. تخضع شروط الإلغاء لسياسة الإلغاء والاسترداد المعمول بها لهذه الخدمة. يلتزم الراكب والسائق بالتصرف بأمان واحترام متبادل أثناء الرحلة، وتُتاح الخدمة وفق المناطق المدعومة حالياً.</p>
    </Section>

    <Section title="6. شروط إرسال الطرود">
      <p>تتوفر خدمة إرسال الطرود حصرياً لمستخدم يحمل تذكرة سفر سارية على رحلة الباص المراد إرسال الطرد عليها، إذ يُشحن الطرد على نفس رحلة المستخدم ومع نفس الشركة الناقلة لها. يُعدّ حامل التذكرة صاحب الطرد والمستلم الافتراضي له في مدينة الوجهة، ما لم يحدِّد عند الإرسال شخصاً آخر مفوَّضاً لاستلامه. يلتزم المرسل بتقديم بيانات صحيحة عن المرسل والمستلم ووصف دقيق لمحتويات الطرد ووزنه، ويُمنع إرسال المواد المحظورة أو الخاضعة لقيود قانونية أو تنظيمية. تقع مسؤولية التغليف المناسب على عاتق المرسل ما لم يُنص على خلاف ذلك.</p>
      <p>يجب تسليم الطرد فعلياً لمكتب الشركة الناقلة في مدينة المغادرة قبل موعد انطلاق الرحلة بـ30 دقيقة على الأقل؛ تسجيل الطلب إلكترونياً لا يُعدّ استلاماً فعلياً أو اعتماداً للشحن قبل إتمام هذه الخطوة في المكتب. بعد انقضاء هذه المهلة دون تسليم الطرد، يخضع قبوله لاعتماد تجاوز استثنائي من الجهة المخوَّلة بذلك، وقد يُرفض الاستلام أو يُعاد الطرد للمرسل. في حال التأخير أو فشل التسليم أو فقدان أو تلف الطرد، تُراجَع الحالة وفق سياسة الشركة الناقلة المعنية، وأي تعويض يخضع لتقييم الحالة ولا تُحدَّد نسبته مسبقاً في هذه الشروط.</p>
    </Section>

    <Section title="7. شروط نقل البضائع">
      <p>يلتزم طالب الخدمة بتقديم وصف دقيق للبضائع ووزنها وأبعادها ومواقع الاستلام والتسليم. تُحدَّد المركبة المناسبة وفق طبيعة البضاعة وحجمها. تقع مسؤولية التحميل والتفريغ وفق الاتفاق بين الطرفين أو سياسة مزوّد الخدمة. يُمنع نقل البضائع المحظورة قانونياً. يلتزم السائق أو مزوّد الخدمة بتنفيذ الطلب وفق البيانات المقدَّمة، وتخضع التعديلات والإلغاءات والمطالبات المتعلقة بالتلف أو النزاعات للمراجعة حالة بحالة.</p>
    </Section>

    <Section title="8. الأسعار والدفع">
      <p>تُعرض الأسعار أو تُؤكَّد وفق الخدمة المختارة قبل إتمام الحجز أو الطلب. تختلف طريقة الدفع المتاحة بحسب الخدمة، ويجب على المستخدم اتباع تعليمات الدفع كما تظهر داخل التطبيق أو الموقع. يُعتمد حالياً التحويل البنكي المباشر في بعض الخدمات، ويخضع التحقق منه لمراجعة الإيصال المرفوع. أي رسوم إضافية يجب الإفصاح عنها قبل تأكيد الطلب.</p>
    </Section>

    <Section title="9. الإلغاء والاسترداد">
      <p>تخضع عمليات الإلغاء والاسترداد لكل خدمة لسياسة الإلغاء والاسترداد المنفصلة. يُرجى مراجعتها قبل إتمام أي حجز أو طلب.</p>
    </Section>

    <Section title="10. المسؤوليات">
      <p>لا تملك سافر أو تُشغّل أي أسطول مركبات (باصات، مركبات نقل بضائع، أو مركبات مشاوير)؛ يقتصر دورها على الربط الرقمي بين طالب الخدمة والجهة أو الشخص الفعلي المقدّم لها (شركة النقل، مقدم خدمة النقل، أو السائق). تقع مسؤولية استيفاء كافة التراخيص والتصاريح والموافقات التنظيمية اللازمة لتقديم خدمة النقل أو التوصيل على عاتق تلك الجهة أو الشخص حصراً، وليس على سافر. تعتمد سافر في اعتماد مقدمي الخدمة على المنصة على التحقق المسبق من استيفائهم للتراخيص والتصاريح المطلوبة قبل إتاحتهم للمستخدمين.</p>
      <p>تعمل سافر كمنصة رقمية تُسهّل الوصول إلى خدمات النقل والتوصيل المقدَّمة من شركات النقل والسائقين ومزوّدي خدمات التوصيل ونقل البضائع. تقع مسؤولية تنفيذ الخدمة الفعلية (الرحلة، المشوار، التوصيل، أو النقل) على الطرف المشغّل لها. يلتزم المستخدم بتقديم بيانات صحيحة وبالتصرف وفق هذه الشروط. يخضع تحديد المسؤولية في أي حالة نزاع للمراجعة وفق طبيعة الخدمة والجهة المشغّلة لها.</p>
      <p>بمجرد اكتمال المعاملة (تأكيد الحجز وإتمام الرحلة، أو تسليم الطرد أو البضاعة، أو انتهاء المشوار)، يقتصر دور سافر على كونها وسيطاً رقمياً يوفّر أداة الحجز والدفع والتوثيق، ولا تُعدّ طرفاً في تنفيذ الخدمة نفسها. أي خلاف أو نزاع ينشأ بين المستخدم وبين الجهة المشغّلة للخدمة (شركة النقل أو السائق أو مزوّد الخدمة) يبقى قائماً بين الطرفين مباشرة، ولا تتحمل سافر عنه أي مسؤولية بعد اكتمال المعاملة.</p>
    </Section>

    <Section title="11. الشكاوى وتسوية النزاعات">
      <p>يمكن للمستخدم تقديم شكوى عبر صفحة "اتصل بنا" مع ذكر رقم الحجز أو الطلب وتفاصيل المشكلة. تُراجَع الشكاوى ويتم الرد عليها خلال فترة زمنية معقولة، وتُحال الحالات التي تتطلب تنسيقاً مع شركة النقل أو مزوّد الخدمة إلى الجهة المعنية للمتابعة. تقديم سافر للمساعدة في تيسير التواصل بين الطرفين لا يُنشئ عليها أي مسؤولية عن نتيجة النزاع أو الفصل فيه، إذ تبقى تسوية الخلاف وموضوعه بين المستخدم والجهة المشغّلة للخدمة مباشرة.</p>
    </Section>

    <Section title="12. القانون المعمول به">
      <p>تخضع هذه الشروط لقوانين جمهورية السودان، وذلك وفق المراجعة القانونية المعتمدة.</p>
    </Section>

    <Section title="13. التواصل">
      <p>لأي استفسار حول هذه الشروط، يُرجى التواصل معنا عبر صفحة "اتصل بنا".</p>
    </Section>
  </ModalBase>
);

/* ═══════════════════════════════════════════════════════════
   PrivacyPolicyContent — shared by the modal (logged-in app/site
   context) and the standalone /privacy-policy page (Google Play's
   Data Safety form requires a directly-loadable URL, not a JS modal).
═══════════════════════════════════════════════════════════ */
export const PrivacyPolicyContent = () => (
  <>
    <p className="text-xs text-gray-500">{lastUpdated}</p>

    <Section title="1. البيانات التي نجمعها">
      <ul className="space-y-1.5 list-none">
        <Li>الاسم الكامل ورقم الهاتف عند التسجيل أو الحجز.</Li>
        <Li>رقم واتساب للتحقق من الحساب واستلام التذكرة والإشعارات.</Li>
        <Li>بيانات الحجز (المسار، التاريخ، المقاعد) عند حجز رحلات الباص.</Li>
        <Li>بيانات طلب المشوار (نقطة الانطلاق والوجهة) عند استخدام خدمة المشاوير.</Li>
        <Li>بيانات إرسال الطرد (المرسل، المستلم، وصف الطرد) ورقم تذكرة السفر المرتبطة به عند استخدام خدمة إرسال الطرود.</Li>
        <Li>بيانات طلب نقل البضائع (نوع البضاعة، مواقع الاستلام والتسليم) عند استخدام خدمة نقل البضائع.</Li>
        <Li>إيصال الدفع الذي يرفعه المستخدم للتحقق من عملية الدفع.</Li>
        <Li>الموقع الجغرافي عند استخدامه لتحديد نقطة الانطلاق في خدمات المشاوير أو نقل البضائع، بموافقة المستخدم عبر إذن الموقع.</Li>
      </ul>
    </Section>

    <Section title="2. كيف نستخدم بياناتك">
      <ul className="space-y-1.5 list-none">
        <Li>تسجيل الحساب والتحقق عبر واتساب.</Li>
        <Li>إتمام حجز رحلات الباص وإصدار التذكرة الإلكترونية.</Li>
        <Li>إدارة طلبات المشاوير وتنسيقها مع السائقين.</Li>
        <Li>تنسيق استلام وتسليم الطرود مع الشركة الناقلة المشغّلة لرحلة التذكرة المرتبطة بالطرد.</Li>
        <Li>تنسيق طلبات نقل البضائع.</Li>
        <Li>التحقق من عمليات الدفع.</Li>
        <Li>إرسال الإشعارات المتعلقة بالحجوزات والطلبات.</Li>
        <Li>الرد على طلبات الدعم الفني.</Li>
        <Li>الحفاظ على أمان المنصة ومنع إساءة الاستخدام.</Li>
      </ul>
    </Section>

    <Section title="3. مشاركة البيانات">
      <p>لا تبيع سافر بيانات المستخدمين لأطراف ثالثة. يتم مشاركة البيانات اللازمة لتنفيذ الخدمة فقط مع الجهة المشغّلة لها — مثل شركة النقل المعنية بالرحلة (للحجز وإرسال الطرود معاً، كونهما يعتمدان على نفس الرحلة)، أو السائق المكلَّف بالمشوار، أو مزوّد خدمة نقل البضائع — وذلك بالقدر اللازم لإتمام الخدمة والتحقق عند الصعود أو الاستلام. قد تُستخدم خدمات تقنية من أطراف ثالثة (مثل واتساب لإرسال رسائل التحقق والتذاكر) باعتبارها مزوّد خدمة تقنية، وليس جهة تُشارَك معها بياناتك لأغراض أخرى.</p>
    </Section>

    <Section title="4. الموقع الجغرافي">
      <p>تطلب سافر إذن الوصول إلى الموقع الجغرافي فقط عند استخدام الميزات التي تتطلبه، مثل تحديد نقطة الانطلاق في خدمات المشاوير أو نقل البضائع. لا تُجمع بيانات الموقع في الخلفية أو بشكل مستمر، ويمكن للمستخدم رفض إذن الموقع واستخدام الطرق البديلة لإدخال العنوان حيثما كانت متاحة.</p>
    </Section>

    <Section title="5. الاحتفاظ بالبيانات">
      <p>تحتفظ سافر ببيانات الحجوزات والطلبات للمدة اللازمة لتقديم الخدمة ولأغراض المحاسبة وحل النزاعات والامتثال للمتطلبات القانونية المعمول بها. عند حذف الحساب، يتم إلغاء تفعيله وإخفاء البيانات الشخصية المرتبطة به وفق الإجراء الموضّح في قسم "حذف الحساب" أدناه، مع الاحتفاظ بالسجلات المالية/المحاسبية غير المرتبطة بالهوية الشخصية للمدة التي يفرضها القانون.</p>
    </Section>

    <Section title="6. حماية البيانات">
      <ul className="space-y-1.5 list-none">
        <Li>تشفير الاتصال بين التطبيق/الموقع والخوادم عبر HTTPS/TLS.</Li>
        <Li>تخزين الرقم السري وبيانات الدخول بصيغة مشفّرة غير قابلة للقراءة المباشرة.</Li>
        <Li>صلاحيات وصول محدودة إلى البيانات داخل الفريق التقني.</Li>
        <Li>إدارة الجلسات وإمكانية إبطال رموز الدخول عند تسجيل الخروج أو حذف الحساب.</Li>
      </ul>
    </Section>

    <Section title="7. حقوقك">
      <ul className="space-y-1.5 list-none">
        <Li>حق الاطلاع على بياناتك الشخصية المحفوظة لدينا.</Li>
        <Li>حق طلب تصحيح البيانات غير الدقيقة.</Li>
        <Li>حق طلب حذف حسابك وبياناتك الشخصية.</Li>
        <Li>حق الاستفسار عن كيفية معالجة بياناتك عبر التواصل معنا.</Li>
      </ul>
    </Section>

    <Section title="8. حذف الحساب">
      <p>
        يمكنك حذف حسابك مباشرة من داخل التطبيق أو الموقع: افتح "حسابي" ثم اختر "حذف الحساب" وأكّد العملية برمز PIN.
        عند التأكيد، يُعطَّل الحساب فوراً وتُخفى بياناته الشخصية. يمكن أيضاً بدء عملية الحذف دون تثبيت التطبيق عبر صفحة{' '}
        <a href="/delete-account" className="text-[#0A4D9D] font-bold underline">safer-sdn.com/delete-account</a>.
      </p>
    </Section>

    <Section title="9. التواصل">
      <p>لممارسة أي من هذه الحقوق أو لأي استفسار يتعلق بالخصوصية، تواصل معنا عبر صفحة "اتصل بنا".</p>
    </Section>
  </>
);

export const PrivacyPolicyModal = ({ onClose, onAccept }) => (
  <ModalBase title="سياسة الخصوصية" icon={<Lock size={16} />} onClose={onClose} onAccept={onAccept}>
    <PrivacyPolicyContent />
  </ModalBase>
);

/* ═══════════════════════════════════════════════════════════
   RefundPolicyModal
═══════════════════════════════════════════════════════════ */
export const RefundPolicyModal = ({ onClose, onAccept }) => (
  <ModalBase title="سياسة الإلغاء والاسترداد" icon={<RotateCcw size={16} />} onClose={onClose} onAccept={onAccept}>
    <p className="text-xs text-gray-500">{lastUpdated}</p>

    <Section title="1. أحكام عامة">
      <p>تختلف شروط الإلغاء والاسترداد بحسب الخدمة المختارة (حجز رحلة، مشاوير، إرسال طرود، نقل بضائع)، والجهة المشغّلة لها، وحالة الدفع، والسياسة المعتمدة لكل خدمة. يُرجى مراجعة الشروط الخاصة بكل خدمة أدناه قبل إتمام الحجز أو الطلب.</p>
    </Section>

    <Section title="2. إلغاء رحلات الباص">
      <p>يمكن طلب إلغاء الحجز قبل موعد المغادرة من صفحة "حجوزاتي" أو بالتواصل مع الشركة الناقلة مباشرة. تخضع شروط قبول الإلغاء ونسبة الاسترداد لسياسة الشركة الناقلة المشغّلة للرحلة. في حال إلغاء الرحلة من قِبل الشركة الناقلة نفسها، يُراجَع حق المسافر في الاسترداد أو إعادة الجدولة وفق سياسة تلك الشركة. حالات عدم الحضور تخضع لذات السياسة.</p>
    </Section>

    <Section title="3. إلغاء المشاوير">
      <p>يمكن إلغاء طلب المشوار قبل قبول أحد السائقين له دون أي التزام. بعد قبول السائق للطلب، قد تختلف شروط الإلغاء، وتُراجَع الحالات التي يُلغي فيها السائق الطلب أو لا يحضر الراكب وفق سياسة الخدمة.</p>
    </Section>

    <Section title="4. إلغاء إرسال الطرود">
      <p>يمكن إلغاء طلب إرسال الطرد قبل تسليمه فعلياً لمكتب الشركة الناقلة دون قيود. إذا لم يُسلَّم الطرد للمكتب قبل موعد انطلاق الرحلة بـ30 دقيقة على الأقل، يُعدّ الطلب منتهي المهلة تلقائياً ولا يُشحن ضمن تلك الرحلة إلا باعتماد تجاوز استثنائي من الجهة المخوَّلة بذلك لدى الشركة الناقلة. بعد الاستلام الفعلي من المكتب أو أثناء كون الطرد قيد النقل، تخضع إمكانية الإلغاء لسياسة الشركة الناقلة المعنية. في حالات فشل التسليم أو رفض الاستلام، تُتَّبع إجراءات إعادة الطرد إلى المرسل وفق الحالة، وتُراجَع أهلية الاسترداد حالة بحالة.</p>
    </Section>

    <Section title="5. إلغاء نقل البضائع">
      <p>يمكن إلغاء طلب نقل البضائع قبل تخصيص مركبة له دون قيود. بعد تأكيد السائق أو تخصيص المركبة، أو بعد بدء التحميل أو انطلاق النقل، تخضع شروط الإلغاء وأي رسوم محتملة لمراجعة الحالة وسياسة مزوّد الخدمة.</p>
    </Section>

    <Section title="6. الاسترداد">
      <p>تُحدَّد أهلية الاسترداد بعد مراجعة الحالة من قِبل الجهة المشغّلة للخدمة المعنية. عند الموافقة على الاسترداد، يتم عبر نفس وسيلة الدفع المستخدمة أصلاً. لأي استفسار أو تصعيد، يُرجى التواصل معنا عبر صفحة "اتصل بنا".</p>
      <PendingApprovalNotice>
        نسب الاسترداد والرسوم الإدارية ومدد المعالجة لم تُعتمد بعد رسمياً عبر جميع الخدمات، وستُحدَّث هذه الصفحة فور اعتمادها من الإدارة.
      </PendingApprovalNotice>
    </Section>
  </ModalBase>
);

/* ═══════════════════════════════════════════════════════════
   FAQModal
═══════════════════════════════════════════════════════════ */
const FAQItem = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-gray-800 hover:bg-gray-50 transition-colors text-right"
      >
        <span className="text-right">{q}</span>
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
          className={`flex-shrink-0 mr-2 transition-transform ${open ? '-rotate-180' : ''}`}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      {open && <div className="px-4 pb-3 text-sm text-gray-600 leading-relaxed">{a}</div>}
    </div>
  );
};

const FAQ_GROUPS = [
  { label: 'عام', items: [
    { q: 'ما هي منصة سافر؟', a: 'سافر منصة رقمية لخدمات النقل والسفر والشحن في السودان، تشمل حجز رحلات الباص، والمشاوير داخل المدينة، وإرسال الطرود، ونقل البضائع.' },
    { q: 'هل يلزمني إنشاء حساب لاستخدام سافر؟', a: 'يمكنك حجز رحلات الباص كضيف دون إنشاء حساب. أما خدمات المشاوير وإرسال الطرود ونقل البضائع فتتطلب تسجيل الدخول.' },
    { q: 'كيف أنشئ حساباً جديداً؟', a: 'اختر التسجيل، وأدخل بياناتك المطلوبة، ثم أكمل التحقق عبر واتساب وفق خطوات التطبيق، وقم بإعداد الرقم السري.' },
    { q: 'ماذا أفعل إذا نسيت الرقم السري؟', a: 'استخدم خيار «نسيت الرقم السري؟» واتبع خطوات استعادة الحساب المتاحة.' },
  ]},
  { label: 'حجز رحلات الباص', items: [
    { q: 'كيف أحجز رحلة باص؟', a: 'اختر «حجز رحلة»، وحدد مدينة المغادرة والوجهة والتاريخ، ثم اختر الرحلة والمقاعد وأكمل الحجز.' },
    { q: 'هل يمكنني اختيار مقعد بعينه؟', a: 'نعم، في الرحلات التي تدعم اختيار المقاعد، يمكنك اختيار المقعد المتاح أثناء الحجز.' },
    { q: 'كيف أستلم تذكرتي؟', a: 'بعد تأكيد الحجز واستكمال متطلبات الدفع، يمكنك الحصول على التذكرة الإلكترونية وفق طريقة التسليم المتاحة.' },
    { q: 'ماذا أفعل إذا لم تصل تذكرتي؟', a: 'راجع تفاصيل الحجز أو تواصل مع الدعم مستخدماً مرجع الحجز.' },
  ]},
  { label: 'المشاوير داخل المدينة', items: [
    { q: 'كيف أطلب مشواراً داخل المدينة؟', a: 'سجّل الدخول، واختر «مشاوير»، وحدد موقع الانطلاق والوجهة، ثم اتبع خطوات الطلب المتاحة.' },
    { q: 'هل يمكنني إلغاء المشوار؟', a: 'تخضع إمكانية الإلغاء وحالات الرسوم لمرحلة الطلب وسياسة الخدمة المعروضة.' },
  ]},
  { label: 'إرسال الطرود', items: [
    { q: 'هل يمكنني إرسال طرد دون تذكرة سفر؟', a: 'لا، تتوفر خدمة إرسال الطرود حالياً حصراً لحاملي تذكرة سفر سارية، إذ يُشحن الطرد على نفس رحلة المستخدم ومع نفس الشركة الناقلة لها.' },
    { q: 'كيف أرسل طرداً؟', a: 'سجّل الدخول، واختر «إرسال طرد»، وستُحدَّد تذكرتك السارية تلقائياً (أو تختارها إن كان لديك أكثر من تذكرة)، ثم أدخل بيانات الطرد ومكتبي الاستلام والتسليم.' },
    { q: 'ما آخر موعد لتسليم الطرد قبل سفر الرحلة؟', a: 'يجب تسليم الطرد فعلياً لمكتب الشركة الناقلة قبل موعد انطلاق الرحلة بـ30 دقيقة على الأقل. تسجيل الطلب إلكترونياً وحده لا يكفي لاعتماد الشحن.' },
    { q: 'من يستلم الطرد في مدينة الوجهة؟', a: 'أنت بصفتك صاحب التذكرة، ما لم تحدد عند الإرسال شخصاً آخر مفوَّضاً بالاستلام.' },
    { q: 'هل يمكنني متابعة حالة الطرد؟', a: 'إذا كانت ميزة المتابعة متاحة في النسخة الحالية، يمكنك الاطلاع على حالة طلبك من داخل التطبيق.' },
  ]},
  { label: 'نقل البضائع', items: [
    { q: 'كيف أطلب نقل بضائع؟', a: 'سجّل الدخول، واختر «نقل عام»، ثم أدخل نوع البضائع ومعلومات الحمولة وموقع الاستلام والتسليم.' },
    { q: 'كيف يتم تحديد تكلفة النقل؟', a: 'تُحدَّد التكلفة وفق آلية التسعير أو عرض السعر المعتمد للخدمة، ويجب إظهار السعر أو تأكيده قبل إتمام الطلب.' },
  ]},
  { label: 'الدفع والدعم', items: [
    { q: 'ما طرق الدفع المتاحة؟', a: 'تظهر طرق الدفع المدعومة أثناء إتمام الحجز أو الطلب، وقد تختلف بحسب الخدمة.' },
    { q: 'كيف أتواصل مع الدعم الفني؟', a: 'افتح «اتصل بنا» واستخدم إحدى وسائل التواصل المعروضة.' },
  ]},
];

export const FAQModal = ({ onClose }) => (
  <ModalBase title="الأسئلة الشائعة" icon={<HelpCircle size={16} />} onClose={onClose}>
    {FAQ_GROUPS.map(({ label, items }) => (
      <div key={label} className="space-y-3">
        <h3 className="font-black text-[#0A4D9D] text-[13px]">{label}</h3>
        {items.map(({ q, a }) => <FAQItem key={q} q={q} a={a} />)}
      </div>
    ))}
  </ModalBase>
);

/* ═══════════════════════════════════════════════════════════
   ContactModal
═══════════════════════════════════════════════════════════ */
const SUPPORT_CATEGORIES = [
  'حجز رحلة', 'مشاوير', 'إرسال طرد', 'نقل عام',
  'الحساب وتسجيل الدخول', 'الدفع والاسترداد', 'مشكلة تقنية', 'استفسار عام',
];

export const ContactModal = ({ onClose }) => (
  <ModalBase title="اتصل بنا" icon={<Phone size={16} />} onClose={onClose}>
    <p className="text-gray-700 text-sm font-semibold">نحن هنا لمساعدتك في خدمات السفر والنقل والشحن</p>

    <div className="space-y-3">
      {[
        { Icon: MessageCircle, label: 'واتساب', value: WHATSAPP_DISPLAY, href: whatsappUrl(), color: 'bg-green-50 border-green-200 text-green-700' },
        { Icon: Phone,         label: 'اتصال هاتفي', value: WHATSAPP_DISPLAY, href: PHONE_TEL, color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
        { Icon: Mail,          label: 'البريد الإلكتروني', value: SUPPORT_EMAIL, href: `mailto:${SUPPORT_EMAIL}`, color: 'bg-blue-50 border-blue-200 text-blue-700' },
      ].map(({ Icon, label, value, href, color }) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer"
          className={`flex items-center gap-3.5 p-4 rounded-xl border ${color} hover:opacity-80 transition-opacity`}>
          <Icon size={22} strokeWidth={2} className="flex-shrink-0" />
          <div>
            <p className="text-xs font-bold opacity-70">{label}</p>
            <p className="font-bold text-base mt-0.5" dir="ltr">{value}</p>
          </div>
          <svg className="w-4 h-4 mr-auto opacity-50 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
          </svg>
        </a>
      ))}
    </div>

    <div>
      <p className="text-xs font-bold text-gray-700 mb-2">بخصوص أي خدمة؟</p>
      {/* Tapping a category opens WhatsApp with it pre-filled — there's no
          ticket-routing backend to assign categories to, so this is the
          smallest way to let the support team know the topic up front
          without building new infrastructure. */}
      <div className="flex flex-wrap gap-2">
        {SUPPORT_CATEGORIES.map((c) => (
          <a key={c} href={whatsappUrl(`أحتاج مساعدة بخصوص: ${c}`)} target="_blank" rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#0A4D9D] text-xs font-bold hover:bg-blue-100 transition-colors">
            {c}
          </a>
        ))}
      </div>
    </div>
  </ModalBase>
);

/* ═══════════════════════════════════════════════════════════
   AboutModal
═══════════════════════════════════════════════════════════ */
export const AboutModal = ({ onClose }) => (
  <ModalBase title="حول سافر" icon={<img src="/logo.png" alt="" className="w-5 h-5 rounded-md object-cover" />} onClose={onClose}>
    <div className="text-center py-4">
      <img src="/logo.png" alt="سافر" className="w-20 h-20 rounded-[22px] object-cover mx-auto mb-4 shadow-lg" />
      <h3 className="text-xl font-black text-gray-900">سافر</h3>
      <p className="text-sm text-gray-800 mt-1">منصة متكاملة لخدمات النقل والسفر والشحن</p>
    </div>

    <p className="text-sm text-gray-800 text-center leading-relaxed">
      سافر منصة رقمية تجمع خدمات النقل والسفر والتوصيل في السودان، وتتيح للمستخدمين حجز رحلات الباص بين المدن،
      وطلب المشاوير داخل المدينة، وإرسال الطرود، وطلب خدمات نقل البضائع من خلال تطبيق واحد سهل الاستخدام.
    </p>

    <div className="bg-gray-50 rounded-xl p-4 space-y-2.5 text-sm">
      {[
        { k: 'الشركة',        v: 'Safer App' },
        { k: 'سنة التأسيس',   v: '2026' },
        { k: 'الدولة',        v: 'السودان' },
        { k: 'الخدمات',       v: 'حجز رحلات الباص، مشاوير داخل المدينة، إرسال الطرود، نقل البضائع' },
        { k: 'التغطية',       v: 'المدن والمناطق المدعومة داخل السودان' },
        { k: 'الإصدار',       v: packageJson.version },
      ].map(({ k, v }) => (
        <div key={k} className="flex justify-between gap-4">
          <span className="text-gray-800 font-semibold flex-shrink-0">{k}</span>
          <span className="font-bold text-gray-900 text-left">{v}</span>
        </div>
      ))}
    </div>

    <p className="text-xs text-gray-500 text-center">
      جميع الحقوق محفوظة © 2026 Safer App
    </p>
  </ModalBase>
);
